import { useMemo, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { Printer } from '@capgo/capacitor-printer';
import { Button, SegmentedControl, Stack, Switch } from '@mantine/core';
import { IconPrinter } from '@tabler/icons-react';
import { Place, PlaceType, Trip } from '../../lib/interfaces';
import { addressForPlace, dateRange, headlineForPlace, isLodging, placeTypeFromPlace } from '../../lib/placeFunctions';
import { isIdea, TSDate } from '../../lib/time';
import { getLogger } from '../../lib/logger';
import { useTripPlacesById } from '../../lib/hooks';
import { computeDaySlots, computeLodgingBars } from '../TripCalendar/tripCalendarLogic';
import styles from './PrintItinerary.module.css';

type DaySection = { day: TSDate, lodging: Place[], items: Place[] };
type ItineraryFormat = 'calendar' | 'list';
const WEEKDAYS = [ 'Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat' ];
// A hotel slice must be at least this many days wide before it shows the address.
const MIN_ADDRESS_DAYS = 1.0;

// Self-contained grouping for the print agenda (no coupling to the interactive TripActivityList).
// Mirrors its within-day ordering: dayorder, then creation time as a tiebreaker.
function sortWithinDay(a: Place, b: Place): number {
    const ao = a.dayorder ?? Number.MAX_SAFE_INTEGER;
    const bo = b.dayorder ?? Number.MAX_SAFE_INTEGER;
    if (ao !== bo) {
        return ao - bo;
    }
    return (a.created?.toMillis() ?? 0) - (b.created?.toMillis() ?? 0);
}

function effectiveEnd(p: Place): TSDate | undefined {
    const s = p.startdatetime;
    if (!s) {
        return undefined;
    }
    return p.enddatetime && !p.enddatetime.isBefore(s) ? p.enddatetime : s;
}

function buildItinerary(trip: Trip, places: Place[]): { days: DaySection[], ideas: Place[], lodgings: Place[] } {
    const scheduled = places.filter(p => p.startdatetime && !isIdea(p.startdatetime));
    const ideas = places
        .filter(p => isIdea(p.startdatetime))
        .sort((a, b) => (a.dayorder ?? Number.MAX_SAFE_INTEGER) - (b.dayorder ?? Number.MAX_SAFE_INTEGER));

    let min: TSDate | undefined;
    let max: TSDate | undefined;
    for (const p of scheduled) {
        const start = p.startdatetime!;
        const end = effectiveEnd(p)!;
        if (!min || start.isBefore(min)) {
            min = start;
        }
        if (!max || end.isAfter(max)) {
            max = end;
        }
    }
    if (trip.startdate && !isIdea(trip.startdate) && (!min || trip.startdate.isBefore(min))) {
        min = trip.startdate;
    }

    const days: DaySection[] = [];
    if (min && max) {
        for (let d = min; !d.isAfter(max); d = d.plus({ days: 1 })) {
            const onDay = d;
            const lodging = scheduled
                .filter(p => isLodging(p) && dateRange(p).some(x => x.equals(onDay)))
                .sort((a, b) => a.startdatetime!.compareTo(b.startdatetime!));
            const items = scheduled
                .filter(p => !isLodging(p) && p.startdatetime!.equals(onDay))
                .sort(sortWithinDay);
            days.push({ day: onDay, lodging, items });
        }
    }
    const lodgings = scheduled.filter(p => isLodging(p) && p.startdatetime && p.enddatetime);
    return { days, ideas, lodgings };
}

// Lay the day sections out into Sunday-aligned weeks for the calendar grid, padding the first and
// last weeks with out-of-range cells (section === undefined).
type CalCell = { day: TSDate, section?: DaySection };
function buildWeeks(days: DaySection[]): CalCell[][] {
    if (days.length === 0) {
        return [];
    }
    const byKey = new Map(days.map(d => [ d.day.toString(), d ]));
    const first = days[0].day;
    const last = days[days.length - 1].day;
    const leadPad = first.toLocalDateTime().weekday % 7; // Luxon Mon=1..Sun=7 -> Sun=0
    const trailPad = 6 - (last.toLocalDateTime().weekday % 7);
    const gridStart = first.minus({ days: leadPad });
    const gridEnd = last.plus({ days: trailPad });

    const cells: CalCell[] = [];
    for (let d = gridStart; !d.isAfter(gridEnd); d = d.plus({ days: 1 })) {
        cells.push({ day: d, section: byKey.get(d.toString()) });
    }
    const weeks: CalCell[][] = [];
    for (let i = 0; i < cells.length; i += 7) {
        weeks.push(cells.slice(i, i + 7));
    }
    return weeks;
}

const TYPE_ICON: Partial<Record<PlaceType, string>> = {
    [PlaceType.Transit]: '✈️',
    [PlaceType.Lodging]: '\u{1F6CF}️',
    [PlaceType.Hotel]: '\u{1F6CF}️',
    [PlaceType.Meal]: '\u{1F374}',
};

function iconFor(p: Place): string {
    const type = placeTypeFromPlace(p);
    return (type && TYPE_ICON[type]) || '•';
}

function dayLabel(d: TSDate): { weekday: string, date: string } {
    const dt = d.toLocalDateTime();
    return {
        weekday: dt.toLocaleString({ weekday: 'long' }),
        date: dt.toLocaleString({ month: 'short', day: 'numeric' }),
    };
}

// Day number for a calendar cell; prefix the month on the 1st so month boundaries read clearly.
function cellDateLabel(d: TSDate): string {
    const dt = d.toLocalDateTime();
    return dt.day === 1 ? dt.toLocaleString({ month: 'short', day: 'numeric' }) : String(dt.day);
}

function rangeLabel(min: TSDate, max: TSDate): string {
    const a = min.toLocalDateTime();
    const b = max.toLocalDateTime();
    const year = b.toLocaleString({ year: 'numeric' });
    if (a.hasSame(b, 'day')) {
        return `${a.toLocaleString({ month: 'long', day: 'numeric' })}, ${year}`;
    }
    return `${a.toLocaleString({ month: 'short', day: 'numeric' })} – `
        + `${b.toLocaleString({ month: 'short', day: 'numeric' })}, ${year}`;
}

function ItineraryItem({ place }: { place: Place }) {
    const meta = addressForPlace(place);
    return (
        <div className={styles.item}>
            <span className={styles.itemIcon}>{iconFor(place)}</span>
            <div className={styles.itemBody}>
                <p className={styles.itemTitle}>{headlineForPlace(place)}</p>
                { meta && <p className={styles.itemMeta}>{meta}</p> }
                { place.review && <p className={styles.itemNotes}>{place.review}</p> }
            </div>
        </div>
    );
}

function IdeasSection({ ideas }: { ideas: Place[] }) {
    if (ideas.length === 0) {
        return null;
    }
    return (
        <section className={styles.section}>
            <h2 className={styles.sectionHeading}>Ideas</h2>
            { ideas.map(idea => <ItineraryItem key={idea.docid} place={idea} />) }
        </section>
    );
}

function ListView({ days, ideas, showAddresses }: { days: DaySection[], ideas: Place[], showAddresses: boolean }) {
    return (
        <>
            { days.map(({ day, lodging, items }) => {
                const { weekday, date } = dayLabel(day);
                const empty = lodging.length === 0 && items.length === 0;
                return (
                    <section key={day.toString()} className={styles.day}>
                        <h2 className={styles.dayHeading}>
                            <span className={styles.dayWeekday}>{weekday}</span> · {date}
                        </h2>
                        { lodging.map(l => {
                            const address = showAddresses ? addressForPlace(l) : '';
                            return (
                                <div key={l.docid} className={styles.lodging}>
                                    <span className={styles.lodgingLabel}>Stay</span>
                                    <span className={styles.lodgingText}>
                                        {headlineForPlace(l)}{address ? ` — ${address}` : ''}
                                    </span>
                                </div>
                            );
                        }) }
                        { items.map(item => <ItineraryItem key={item.docid} place={item} />) }
                        { empty && <p className={styles.emptyDay}>No plans</p> }
                    </section>
                );
            }) }
            <IdeasSection ideas={ideas} />
        </>
    );
}

function CalendarView(
    { days, ideas, lodgings, showAddresses }:
    { days: DaySection[], ideas: Place[], lodgings: Place[], showAddresses: boolean },
) {
    const weeks = useMemo(() => buildWeeks(days), [ days ]);
    const slots = useMemo(() => computeDaySlots(lodgings), [ lodgings ]);
    const tripStart = days[0]?.day;
    const tripEnd = days[days.length - 1]?.day;

    // Hotels span as continuous bars (mirrors the interactive calendar's 14-half-column model);
    // drop the null "no hotel" gap bars so only real stays show.
    const weekBars = useMemo(
        () => (tripStart && tripEnd)
            ? weeks.map(week =>
                computeLodgingBars(week.map(c => c.day), slots, tripStart, tripEnd).filter(b => b.lodging))
            : weeks.map(() => []),
        [ weeks, slots, tripStart, tripEnd ],
    );

    return (
        <>
            <div className={styles.calGrid}>
                <div className={styles.calHeadRow}>
                    { WEEKDAYS.map((w, d) => (
                        <div key={w} className={styles.calHead} style={{ gridColumn: `${2 * d + 1} / ${2 * d + 3}` }}>
                            {w}
                        </div>
                    )) }
                </div>
                { weeks.map((week, wi) => {
                    const bars = weekBars[wi];
                    return (
                        <div key={week[0].day.toString()} className={styles.calWeek}>
                            <div className={styles.calDateRow}>
                                { week.map(({ day, section }, d) => {
                                    const cls = section
                                        ? styles.calDateCell
                                        : `${styles.calDateCell} ${styles.calOutside}`;
                                    return (
                                        <div
                                            key={day.toString()}
                                            className={cls}
                                            style={{ gridColumn: `${2 * d + 1} / ${2 * d + 3}` }}
                                        >
                                            {cellDateLabel(day)}
                                        </div>
                                    );
                                }) }
                            </div>
                            { bars.length > 0 &&
                                <div className={styles.calBars}>
                                    {/* Per-day background cells carry the column dividers through the bar band,
                                        so days without a hotel read as continuous grid cells rather than a blank
                                        white stripe. The spanning bars overlay these (same grid row). */}
                                    { week.map(({ day, section }, d) => {
                                        const cls = section
                                            ? styles.calBarsCell
                                            : `${styles.calBarsCell} ${styles.calOutside}`;
                                        return (
                                            <div
                                                key={`bg-${day.toString()}`}
                                                className={cls}
                                                style={{ gridColumn: `${2 * d + 1} / ${2 * d + 3}`, gridRow: 1 }}
                                            />
                                        );
                                    }) }
                                    { bars.map(bar => {
                                        // span is in half-columns (2 per day), so span / 2 is days. Any slice at
                                        // least the threshold wide shows the full (wrapped) name + address; a
                                        // narrower wedge truncates the name and omits the address.
                                        const wide = (bar.colEnd - bar.colStart) / 2 >= MIN_ADDRESS_DAYS;
                                        const address = addressForPlace(bar.lodging!);
                                        const showAddress = showAddresses && !!address && wide;
                                        const nameClass = wide
                                            ? styles.calBarName
                                            : `${styles.calBarName} ${styles.calBarTruncate}`;
                                        return (
                                            <div
                                                key={`${bar.colStart}-${bar.lodging!.docid}`}
                                                className={styles.calBar}
                                                style={{ gridColumn: `${bar.colStart} / ${bar.colEnd}`, gridRow: 1 }}
                                                title={headlineForPlace(bar.lodging!)}
                                            >
                                                <span className={nameClass}>🛏 {headlineForPlace(bar.lodging!)}</span>
                                                { showAddress && <span className={styles.calBarAddr}>{address}</span> }
                                            </div>
                                        );
                                    }) }
                                </div> }
                            <div className={styles.calCells}>
                                { week.map(({ day, section }, d) => (
                                    <div
                                        key={day.toString()}
                                        className={section ? styles.calCell : `${styles.calCell} ${styles.calOutside}`}
                                        style={{ gridColumn: `${2 * d + 1} / ${2 * d + 3}` }}
                                    >
                                        { section?.items.map(item => (
                                            <div key={item.docid} className={styles.calItem}>
                                                <span className={styles.calItemIcon}>{iconFor(item)}</span>
                                                <span className={styles.calItemText}>{headlineForPlace(item)}</span>
                                            </div>
                                        )) }
                                    </div>
                                )) }
                            </div>
                        </div>
                    );
                }) }
            </div>
            <IdeasSection ideas={ideas} />
        </>
    );
}

// The printable itinerary itself (just the white "sheet"): a clean, non-interactive calendar grid
// or list agenda. Rendered off-screen by PrintItineraryModal and cloned into the print document.
function ItinerarySheet({ trip, places, format, showAddresses, innerRef }: {
    trip: Trip, places: Place[], format: ItineraryFormat, showAddresses: boolean,
    innerRef: React.Ref<HTMLDivElement>,
}) {
    const { days, ideas, lodgings } = useMemo(() => buildItinerary(trip, places), [ trip, places ]);
    const range = days.length > 0 ? rangeLabel(days[0].day, days[days.length - 1].day) : undefined;
    const metaParts = [ trip.location ?? undefined, range ].filter(Boolean);
    return (
        <div className={styles.sheet} ref={innerRef}>
            <div className={styles.titleRow}>
                <h1 className={styles.tripTitle}>{trip.title ?? 'Itinerary'}</h1>
                { metaParts.length > 0 && <p className={styles.tripMeta}>{metaParts.join('  ·  ')}</p> }
            </div>
            { format === 'calendar'
                ? <CalendarView days={days} ideas={ideas} lodgings={lodgings} showAddresses={showAddresses} />
                : <ListView days={days} ideas={ideas} showAddresses={showAddresses} /> }
        </div>
    );
}

// Build a complete, self-contained HTML document from a rendered .sheet element: inline every
// readable stylesheet (the clone keeps its hashed CSS-module class names) plus print-specific rules.
// Used for both web (iframe) and native (Printer.printHtml) printing.
function buildItineraryDoc(sheet: HTMLElement, format: ItineraryFormat, title: string): string {
    let css = '';
    for (const ss of Array.from(document.styleSheets)) {
        try {
            for (const rule of Array.from(ss.cssRules)) {
                css += `${rule.cssText}\n`;
            }
        } catch {
            // Cross-origin stylesheet; rules aren't accessible.
        }
    }
    const safeTitle = title.replace(/[<>&]/g, ' ');
    const pageClass = `${styles.page} ${format === 'calendar' ? styles.wide : ''}`;
    const orientation = format === 'calendar' ? 'landscape' : 'portrait';
    return [
        '<!doctype html><html><head><meta charset="utf-8">',
        `<title>${safeTitle}</title>`,
        `<style>${css}</style>`,
        '<style>',
        // No @page margin: a margin lets the browser fill it with its own header/footer (date / URL /
        // page number) we can't otherwise suppress; whitespace comes from .print-root padding instead.
        `@page { size: ${orientation}; margin: 0; }`,
        'html, body { height: auto !important; width: auto !important; overflow: visible !important;',
        ' margin: 0; padding: 0; background: #fff; }',
        '.print-root { box-sizing: border-box; padding: 5mm 6mm !important; }',
        '</style></head><body>',
        `<div class="print-root ${pageClass}">${sheet.outerHTML}</div>`,
        '</body></html>',
    ].join('');
}

// Native: hand the document to the OS print sheet (UIPrintInteractionController → AirPrint /
// save-to-PDF). Web: print a hidden, static iframe synchronously (no open connections, so iOS
// Safari doesn't stall waiting for the page to go quiescent; synchronous keeps the tap's activation).
async function printItineraryDoc(html: string, title: string, log: ReturnType<typeof getLogger>): Promise<void> {
    if (Capacitor.isNativePlatform()) {
        log.log('printing via native Printer.printHtml');
        await Printer.printHtml({ html, name: title });
        return;
    }
    const frame = document.createElement('iframe');
    frame.setAttribute('aria-hidden', 'true');
    frame.style.cssText = 'position:fixed; right:0; bottom:0; width:0; height:0; border:0;';
    document.body.appendChild(frame);
    const w = frame.contentWindow;
    if (!w) {
        frame.remove();
        log.log('iframe unavailable; falling back to window.print()');
        window.print();
        return;
    }
    let cleaned = false;
    const cleanup = () => { if (!cleaned) { cleaned = true; frame.remove(); } };
    w.addEventListener('afterprint', cleanup);
    w.document.open();
    w.document.write(html);
    w.document.close();
    w.focus();
    w.print();
    setTimeout(cleanup, 60000); // afterprint is unreliable on iOS Safari
}

// The "Print itinerary" options sheet: pick format + whether to show hotel addresses, then print.
// Prints directly (web: browser print; native: the OS print sheet) — there's no separate page to
// view. The itinerary is rendered off-screen purely as the source for the print document.
export function PrintItinerarySheet({ trip }: { trip: Trip }) {
    const places = useTripPlacesById(trip.docid ?? undefined) ?? [];
    const log = useMemo(() => getLogger('print'), []);
    const [ format, setFormat ] = useState<ItineraryFormat>('calendar');
    const [ showAddresses, setShowAddresses ] = useState(true);
    const sheetRef = useRef<HTMLDivElement>(null);
    const title = `${trip.title ?? trip.location ?? 'Trip'} Itinerary`;

    const doPrint = () => {
        const el = sheetRef.current;
        if (!el) {
            return;
        }
        const html = buildItineraryDoc(el, format, title);
        printItineraryDoc(html, title, log).catch(e => log.error(`print failed: ${e}`));
    };

    return (
        <Stack gap="md" px="md" pb="md">
            <SegmentedControl
                fullWidth
                value={format}
                onChange={value => setFormat(value as ItineraryFormat)}
                data={[ { value: 'calendar', label: 'Calendar' }, { value: 'list', label: 'List' } ]}
            />
            <Switch
                label="Hotel addresses"
                checked={showAddresses}
                onChange={event => setShowAddresses(event.currentTarget.checked)}
            />
            <Button leftSection={<IconPrinter size={18} />} onClick={doPrint} disabled={places.length === 0}>
                Print
            </Button>

            {/* Off-screen source for the print document (cloned in buildItineraryDoc). */}
            <div aria-hidden style={{ position: 'fixed', left: '-10000px', top: 0, width: 1024, pointerEvents: 'none' }}>
                <ItinerarySheet
                    trip={trip}
                    places={places}
                    format={format}
                    showAddresses={showAddresses}
                    innerRef={sheetRef}
                />
            </div>
        </Stack>
    );
}
